// P10 — Page Admin Users avec modales Create/Edit/Delete (Phase 2)
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { useT } from "@/lib/i18n";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";

interface User {
  id: number;
  email: string;
  name: string;
  role: "tech" | "sup" | "respo" | "app_admin";
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface UserFormData {
  email: string;
  name: string;
  role: User["role"];
  password: string;
  is_active: boolean;
}

// ⚠️ Fonction (et non const) : un const au niveau module serait figé au
// premier rendu et ne se retraduirait pas au changement de locale.
const ROLES = (t: (key: string) => string): { value: User["role"]; label: string }[] => [
  { value: "tech", label: t("users.role.tech") },
  { value: "sup", label: t("users.role.sup") },
  { value: "respo", label: t("users.role.respo") },
  { value: "app_admin", label: t("users.role.app_admin") },
];

export default function UsersPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const t = useT();
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [forcePwdUser, setForcePwdUser] = useState<User | null>(null);
  const [forcePwdValue, setForcePwdValue] = useState("");
  const [resetEmailUser, setResetEmailUser] = useState<User | null>(null);
  const [resetEmailResult, setResetEmailResult] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["users"],
    queryFn: async (): Promise<User[]> => {
      const r = await fetch(`${API_BASE}/v1/users`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error(`API ${r.status}`);
      return r.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      const r = await fetch(`${API_BASE}/v1/users`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error((await r.json()).detail || `API ${r.status}`);
      return r.json();
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setShowCreate(false);
      toast({ title: ht("Utilisateur créé"), description: created.email, variant: "success" });
    },
    onError: (err) => {
      toast({ title: t("users.toast.error_creation"), description: (err as Error).message, variant: "error" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<UserFormData> }) => {
      const r = await fetch(`${API_BASE}/v1/users/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error((await r.json()).detail || `API ${r.status}`);
      return r.json();
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setEditingUser(null);
      toast({ title: ht("Utilisateur modifié"), description: updated.email, variant: "success" });
    },
    onError: (err) => {
      toast({ title: ht("Erreur modification"), description: (err as Error).message, variant: "error" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`${API_BASE}/v1/users/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(txt || `API ${r.status}`);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setDeletingUser(null);
      toast({ title: ht("Utilisateur supprimé"), variant: "success" });
    },
    onError: (err) => {
      const msg = (err as Error).message;
      const title = msg.startsWith("Impossible de supprimer") ? ht("Suppression impossible") : "Erreur suppression";
      toast({ title, description: msg, variant: "error", duration: 8000 });
    },
  });

  // P45b — Force password
  const forcePwdMutation = useMutation({
    mutationFn: async ({ id, new_password }: { id: number; new_password: string }) => {
      const r = await fetch(`${API_BASE}/v1/users/${id}/force-password`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ new_password }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `API ${r.status}`);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setForcePwdUser(null);
      setForcePwdValue("");
      toast({ title: ht("Mot de passe forcé"), description: ht("L'utilisateur devra utiliser ce nouveau mdp"), variant: "success" });
    },
    onError: (err) => {
      toast({ title: "Erreur", description: (err as Error).message, variant: "error" });
    },
  });

  // P45b — Reset + email
  const resetEmailMutation = useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`${API_BASE}/v1/users/${id}/reset-password-email`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `API ${r.status}`);
      }
      return r.json();
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      setResetEmailResult(data.new_password);
      toast({ title: t("users.toast.email_sent"), description: `Nouveau mdp généré pour ${resetEmailUser?.email}`, variant: "success" });
    },
    onError: (err) => {
      toast({ title: "Erreur", description: (err as Error).message, variant: "error" });
    },
  });

  if (isLoading) return <div className="p-8">{ht("Chargement...")}</div>;
  if (error)
    return <div className="p-8 text-red-600">{error instanceof Error ? error.message : String(error)}</div>;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{ht("Utilisateurs")}</h1>
          <p className="text-muted-foreground">
            Gestion des comptes (RBAC : respo+ requis)
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>{ht("+ Nouvel utilisateur")}</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("common.list")} ({data?.length ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                <th className="px-3 py-2">{ht("ID")}</th>
                <th className="px-3 py-2">{ht("Email")}</th>
                <th className="px-3 py-2">{ht("Nom")}</th>
                <th className="px-3 py-2">{ht("Rôle")}</th>
                <th className="px-3 py-2">{ht("Actif")}</th>
                <th className="px-3 py-2">{ht("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((u) => (
                <tr key={u.id} className="border-b hover:bg-muted/30">
                  <td className="px-3 py-2 font-mono">{u.id}</td>
                  <td className="px-3 py-2">{u.email}</td>
                  <td className="px-3 py-2">{u.name}</td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        u.role === "respo" || u.role === "app_admin"
                          ? "rounded bg-amber-500/15 border border-amber-500/40 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-300"
                          : "rounded bg-blue-500/15 border border-blue-500/40 px-2 py-0.5 text-xs text-blue-700 dark:text-blue-300"
                      }
                    >
                      {t(`users.role.${u.role}`)}
                    </span>
                  </td>
                  <td className="px-3 py-2">{u.is_active ? "✓" : "✗"}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingUser(u)}
                      >{ht("Éditer")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setForcePwdUser(u);
                          setForcePwdValue("");
                        }}
                        data-testid={`force-password-button-${u.id}`}
                      >
                        🔑 Forcer mdp
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setResetEmailUser(u);
                          setResetEmailResult(null);
                        }}
                        data-testid={`reset-email-button-${u.id}`}
                      >
                        {t("users.action.send_password")}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDeletingUser(u)}
                      >{ht("Supprimer")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </CardContent>
      </Card>

      {showCreate && (
        <UserFormModal
          title={ht("Nouvel utilisateur")}
          onClose={() => setShowCreate(false)}
          onSubmit={(data) => createMutation.mutateAsync(data)}
          error={createMutation.error}
          pending={createMutation.isPending}
        />
      )}
      {editingUser && (
        <UserFormModal
          title={`Éditer ${editingUser.email}`}
          initial={editingUserToForm(editingUser)}
          onClose={() => setEditingUser(null)}
          onSubmit={(data) =>
            updateMutation.mutateAsync({ id: editingUser.id, data })
          }
          error={updateMutation.error}
          pending={updateMutation.isPending}
          isEdit
        />
      )}
      {deletingUser && (
        <ConfirmDialog
          title={t("users.confirm.delete_title")}
          description={`Supprimer définitivement l'utilisateur « ${deletingUser.email} » ?`}
          confirmLabel={ht("Supprimer")}
          variant="destructive"
          pending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutateAsync(deletingUser.id)}
          onCancel={() => setDeletingUser(null)}
        />
      )}

      {/* P45b — Modale Force password */}
      {forcePwdUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          data-testid="force-password-modal"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-bold">{ht("🔑 Forcer le mot de passe")}</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Pour <strong>{forcePwdUser.email}</strong> (l'user ne recevra PAS d'email, communiquez-lui
              le mot de passe vous-même).
            </p>
            <Input
              type="text"
              placeholder={ht("Nouveau mot de passe (≥ 8 chars)")}
              value={forcePwdValue}
              onChange={(e) => setForcePwdValue(e.target.value)}
              data-testid="force-password-input"
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setForcePwdUser(null);
                  setForcePwdValue("");
                }}
              >{ht("Annuler")}
              </Button>
              <Button
                onClick={() =>
                  forcePwdMutation.mutate({
                    id: forcePwdUser.id,
                    new_password: forcePwdValue,
                  })
                }
                disabled={forcePwdValue.length < 8 || forcePwdMutation.isPending}
                data-testid="force-password-submit"
              >
                {forcePwdMutation.isPending ? "..." : "Forcer"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* P45b — Modale Reset + email */}
      {resetEmailUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          data-testid="reset-email-modal"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-bold">{ht("📧 Envoyer un nouveau mot de passe")}</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Un nouveau mot de passe sera généré et envoyé par email à <strong>{resetEmailUser.email}</strong>.
            </p>
            {resetEmailResult ? (
              <>
                <div className="mb-4 rounded-md border border-green-500 bg-green-50 p-3 text-sm text-green-900">
                  <p className="font-semibold">{ht("Email envoyé !")}</p>
                  <p className="mt-1 text-xs">{ht("Si l'email échoue, communiquez ce mdp à l'user :")}
                  </p>
                  <p className="mt-1 break-all rounded bg-white p-2 font-mono text-xs">
                    {resetEmailResult}
                  </p>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      setResetEmailUser(null);
                      setResetEmailResult(null);
                    }}
                  >{ht("Fermer")}
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setResetEmailUser(null);
                    setResetEmailResult(null);
                  }}
                >{ht("Annuler")}
                </Button>
                <Button
                  onClick={() => resetEmailMutation.mutate(resetEmailUser.id)}
                  disabled={resetEmailMutation.isPending}
                  data-testid="reset-email-submit"
                >
                  {resetEmailMutation.isPending ? "..." : t("users.action.send")}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function editingUserToForm(u: User): UserFormData {
  return {
    email: u.email,
    name: u.name,
    role: u.role,
    password: "", // pas renvoyé par l'API
    is_active: u.is_active,
  };
}

function UserFormModal({
  title,
  initial,
  onClose,
  onSubmit,
  error,
  pending,
  isEdit = false,
}: {
  title: string;
  initial?: UserFormData;
  onClose: () => void;
  onSubmit: (data: UserFormData) => Promise<unknown>;
  error: unknown;
  pending: boolean;
  isEdit?: boolean;
}) {
  const t = useT();
  const [form, setForm] = useState<UserFormData>(
    initial ?? {
      email: "",
      name: "",
      role: "tech",
      password: "",
      is_active: true,
    },
  );

  const canSubmit = isEdit
    ? form.email.length > 0 && form.name.length > 0
    : form.email.length > 0 &&
      form.name.length > 0 &&
      form.password.length >= 8;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="w-[min(32rem,calc(100vw-2rem))]">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <FormField label={ht("Email")}>
              <input
                type="email"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </FormField>
            <FormField label={ht("Nom")}>
              <input
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </FormField>
            <FormField label={ht("Rôle")}>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.role}
                onChange={(e) =>
                  setForm({ ...form, role: e.target.value as User["role"] })
                }
              >
                {ROLES(t).map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField
              label={isEdit ? t("users.password.keep") : t("users.password.min")}
            >
              <input
                type="password"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={isEdit ? "Laisser vide" : ""}
              />
            </FormField>
            <FormField label="">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm({ ...form, is_active: e.target.checked })
                  }
                />{ht("Actif")}
              </label>
            </FormField>
            {error ? (
              <div className="rounded bg-red-50 p-2 text-sm text-red-700">
                {error instanceof Error ? error.message : String(error)}
              </div>
            ) : null}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>{ht("Annuler")}
            </Button>
            <Button
              onClick={() => onSubmit(form)}
              disabled={!canSubmit || pending}
            >
              {pending ? "Enregistrement..." : isEdit ? "Modifier" : "Créer"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FormField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      {label && <label className="text-xs">{label}</label>}
      {children}
    </div>
  );
}

